import { analyticsBatcher } from './analyticsBatcher';

// Global types are declared in src/renderer/types/global.d.ts

export type CoreWebVitalName =
  | 'first_input_delay'
  | 'largest_contentful_paint'
  | 'first_contentful_paint'
  | 'cumulative_layout_shift'
  | 'interaction_to_next_paint';

export interface CoreWebVitalAttribution {
  /** CSS selector / target of the largest contributing shift (CLS) or element (LCP). */
  largest_shift_target?: string;
  load_state?: string;
  largest_shift_value?: number;
  page_path?: string;
}

/**
 * Track performance metrics (batched → GA4 / GTM).
 * Values are sent as floats so CLS scores in the 0–1 range stay usable.
 */
export const trackPerformance = (
  metricName: string,
  value: number,
  unit?: string,
  attribution?: CoreWebVitalAttribution,
) => {
  analyticsBatcher.addEvent({
    action: 'performance',
    category: 'performance',
    label: metricName,
    value,
    custom_parameters: {
      performance_metric: metricName,
      performance_value: value,
      performance_unit: unit,
      page_path:
        attribution?.page_path ??
        (typeof window !== 'undefined' ? window.location.pathname : undefined),
      ...(attribution?.largest_shift_target
        ? { cls_largest_shift_target: attribution.largest_shift_target }
        : {}),
      ...(attribution?.load_state ? { cls_load_state: attribution.load_state } : {}),
      ...(typeof attribution?.largest_shift_value === 'number'
        ? { cls_largest_shift_value: attribution.largest_shift_value }
        : {}),
    },
  });
};

/**
 * Track Core Web Vitals (GA4 events).
 *
 * CLS must NOT use Math.round on the primary score — typical values are 0.01–0.5
 * and rounding collapses them to 0. Send the float as `metric_value`; for time-based
 * vitals keep integer `value` (ms) for GA4 convention.
 */
export const trackCoreWebVitals = (
  metricName: CoreWebVitalName,
  value: number,
  attribution?: CoreWebVitalAttribution,
) => {
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  const isCls = metricName === 'cumulative_layout_shift';
  const pagePath =
    attribution?.page_path ?? window.location.pathname;

  const payload: Record<string, unknown> = {
    metric_value: value,
    event_category: 'Web Vitals',
    event_label: metricName,
    non_interaction: true,
    page_path: pagePath,
  };

  // GA4 `value` is often treated as an integer; keep ms rounding for LCP/FID/FCP only.
  if (!isCls) {
    payload.value = Math.round(value);
  }
  if (isCls && attribution?.largest_shift_target) {
    payload.cls_largest_shift_target = attribution.largest_shift_target;
  }
  if (isCls && attribution?.load_state) {
    payload.cls_load_state = attribution.load_state;
  }
  if (isCls && typeof attribution?.largest_shift_value === 'number') {
    payload.cls_largest_shift_value = attribution.largest_shift_value;
  }

  window.gtag('event', metricName, payload);
};

/**
 * Track page load performance
 */
export const trackPageLoadPerformance = (loadTime: number, domContentLoaded: number, firstPaint: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'page_load_time', {
      value: Math.round(loadTime),
      custom_parameters: {
        dom_content_loaded: Math.round(domContentLoaded),
        first_paint: Math.round(firstPaint),
        load_time: Math.round(loadTime),
      },
    });
  }
};
